import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api.js';
import { BackupInfo } from '@kcdash/shared';
import { Archive, Plus, Download, RotateCcw, Trash2, Check, Clock } from 'lucide-react';

export const BackupsPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [backups, setBackups] = useState<BackupInfo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);

  const loadBackups = async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const data = await api.getBackups(id);
      setBackups(data);
    } catch (err) {
      console.error('Failed to load backups:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadBackups();
  }, [id]);

  const handleCreateBackup = async () => {
    if (!id) return;
    const notes = prompt('Enter a label/note for this backup:', 'Manual backup');
    if (notes === null) return;

    setIsCreating(true);
    setStatusMessage(null);
    try {
      const created = await api.createBackup(id, notes || undefined);
      setStatusMessage(`Backup '${created.filename}' generated successfully.`);
      await loadBackups();
    } catch (err: any) {
      alert(`Backup failed: ${err.message}`);
    } finally {
      setIsCreating(false);
    }
  };

  const handleRestore = async (backup: BackupInfo) => {
    if (!id) return;
    if (
      !confirm(
        `Are you sure you want to restore from ${backup.filename}? This will overwrite current server configs and gamemode files.`
      )
    ) {
      return;
    }

    try {
      await api.restoreBackup(id, backup.id);
      alert('Backup restored into server directory successfully. Server restart recommended.');
    } catch (err: any) {
      alert(`Restore failed: ${err.message}`);
    }
  };

  const handleDelete = async (backupId: string) => {
    if (!id) return;
    if (!confirm('Are you sure you want to permanently delete this backup file?')) return;

    try {
      await api.deleteBackup(id, backupId);
      await loadBackups();
    } catch (err: any) {
      alert(`Delete failed: ${err.message}`);
    }
  };

  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-neutral-100">Server Backups</h2>
          <p className="text-xs text-neutral-400 mt-1 font-mono">
            Full snapshots of server.toml, custom configs, and gamemodes
          </p>
        </div>

        <button
          onClick={handleCreateBackup}
          disabled={isCreating}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-semibold bg-brand-600 hover:bg-brand-500 text-white transition shadow-sm disabled:opacity-50"
        >
          <Plus className="w-4 h-4" />
          <span>{isCreating ? 'Creating Archive...' : 'Create Backup'}</span>
        </button>
      </div>

      {statusMessage && (
        <div className="p-3 rounded-lg bg-brand-950/60 border border-brand-800 text-brand-300 text-xs flex items-center gap-2">
          <Check className="w-4 h-4 text-brand-400 shrink-0" />
          <span>{statusMessage}</span>
        </div>
      )}

      <div className="bg-panel-card border border-panel-border rounded-xl shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs font-mono">
            <thead className="bg-panel-subtle/80 border-b border-panel-border text-neutral-400 uppercase select-none">
              <tr>
                <th className="px-5 py-3">Archive Filename</th>
                <th className="px-5 py-3">Notes</th>
                <th className="px-5 py-3">Size</th>
                <th className="px-5 py-3">Created</th>
                <th className="px-5 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-panel-border/50 text-neutral-300">
              {isLoading ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-neutral-500">
                    Loading backups...
                  </td>
                </tr>
              ) : backups.length === 0 ? (
                <tr>
                  <td colSpan={5} className="py-12 text-center text-neutral-500">
                    No backup archives found for this server.
                  </td>
                </tr>
              ) : (
                backups.map((b) => (
                  <tr key={b.id} className="hover:bg-panel-cardHover transition">
                    <td className="px-5 py-3.5 flex items-center gap-2.5">
                      <Archive className="w-4 h-4 text-brand-400 shrink-0" />
                      <span className="font-semibold text-neutral-100">{b.filename}</span>
                    </td>
                    <td className="px-5 py-3.5 text-neutral-400">{b.notes || 'Manual Backup'}</td>
                    <td className="px-5 py-3.5 text-neutral-400">{formatSize(b.sizeBytes)}</td>
                    <td className="px-5 py-3.5 text-neutral-500">
                      {new Date(b.createdAt).toLocaleString()}
                    </td>
                    <td className="px-5 py-3.5 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        <a
                          href={`/api/v1/servers/${id}/backups/${b.id}/download`}
                          title="Download ZIP"
                          className="p-1.5 rounded-lg bg-panel-subtle hover:bg-neutral-800 text-neutral-400 hover:text-neutral-200 transition"
                        >
                          <Download className="w-3.5 h-3.5" />
                        </a>

                        <button
                          onClick={() => handleRestore(b)}
                          title="Restore into server"
                          className="p-1.5 rounded-lg bg-panel-subtle hover:bg-amber-950/60 text-neutral-400 hover:text-amber-400 transition"
                        >
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>

                        <button
                          onClick={() => handleDelete(b.id)}
                          title="Delete archive"
                          className="p-1.5 rounded-lg bg-panel-subtle hover:bg-red-950/60 text-neutral-400 hover:text-red-400 transition"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};
